/**
 * Selected v2 tool and the last inspect result (NIfTI header, notices).
 */

import { create } from "zustand";
import { readNiftiHeader, refreshCaseNodes, runVolumeTool, splitError } from "@/ipc/api";
import type { NiftiHeaderInfo, VolumeToolResult } from "@/ipc/contract";
import { openProtocolStep } from "@/pipeline/protocol";
import { usePipelineSession } from "@/pipeline/session";
import { appActions, useAppStore } from "@/store/appStore";
import { parameterIssue, toolRunIssue } from "./validation";
import { nodeAbsPath, useViewerStore } from "@/viewer/viewerStore";
import { toolById, toolsForTab, type ToolTabId } from "./catalog";

export interface ToolNotice {
  tone: "info" | "error";
  message: string;
  detail: string | null;
}

interface ToolState {
  selectedId: string | null;
  header: NiftiHeaderInfo | null;
  headerPath: string | null;
  notice: ToolNotice | null;
  busy: boolean;
  launchOpen: boolean;
  fieldValues: Record<string, string>;
  referenceNodeId: string | null;
  opResult: VolumeToolResult | null;
  selectTool: (id: string) => void;
  selectTabDefault: (tab: ToolTabId) => void;
  openLaunch: () => void;
  closeLaunch: () => void;
  setField: (toolId: string, fieldId: string, value: string) => void;
  setReferenceNodeId: (id: string | null) => void;
  setNotice: (notice: ToolNotice | null) => void;
  runSelected: () => Promise<void>;
  inspectHeader: (path?: string) => Promise<void>;
}

function targetVolumePath(): string | null {
  const manifest = useAppStore.getState().currentCase;
  const selected = useAppStore.getState().selectedNodeId;
  if (manifest && selected) {
    const node = [...manifest.images, ...manifest.masks, ...manifest.atlases].find((n) => n.id === selected);
    const path = nodeAbsPath(manifest, node ?? null);
    if (path) return path;
  }
  return null;
}

export const useToolSession = create<ToolState>((set, get) => ({
  selectedId: null,
  header: null,
  headerPath: null,
  notice: null,
  busy: false,
  launchOpen: false,
  fieldValues: {},
  referenceNodeId: null,
  opResult: null,

  selectTool: (id) => set({ selectedId: id, notice: null, opResult: null, header: null, headerPath: null, referenceNodeId: null }),

  selectTabDefault: (tab) => {
    const current = toolById(get().selectedId ?? "");
    if (current?.tab === tab) return;
    const ready = toolsForTab(tab).find((t) => t.status === "ready");
    set({ selectedId: ready?.id ?? null, notice: null });
  },

  openLaunch: () => set({ launchOpen: true }),
  closeLaunch: () => set({ launchOpen: false }),
  setField: (toolId, fieldId, value) =>
    set((state) => ({ fieldValues: { ...state.fieldValues, [`${toolId}.${fieldId}`]: value } })),
  setReferenceNodeId: (id) => set({ referenceNodeId: id }),

  setNotice: (notice) => set({ notice }),

  inspectHeader: async (path) => {
    if (get().busy) return;
    const caseId = useAppStore.getState().currentCase?.caseId;
    const selectedId = get().selectedId;
    const target = path ?? targetVolumePath();
    if (!target) {
      set({
        notice: {
          tone: "error",
          message: "Select a NIfTI in the Study Explorer, or open a case first.",
          detail: null,
        },
      });
      return;
    }
    set({ busy: true, notice: null });
    try {
      const header = await readNiftiHeader(target);
      if (useAppStore.getState().currentCase?.caseId === caseId && get().selectedId === selectedId && targetVolumePath() === target) {
        set({ header, headerPath: target });
      }
      set({ busy: false });
    } catch (error) {
      const { message, detail } = splitError(error);
      set({ busy: false });
      if (useAppStore.getState().currentCase?.caseId === caseId && get().selectedId === selectedId) set({ notice: { tone: "error", message, detail } });
    }
  },

  runSelected: async () => {
    const tool = toolById(get().selectedId ?? "");
    const state = useAppStore.getState();
    const caseVolumes = state.currentCase ? [...state.currentCase.images, ...state.currentCase.masks, ...state.currentCase.atlases] : [];
    const issue = toolRunIssue(tool, {
      hasCase: Boolean(state.currentCase), hasInput: Boolean(targetVolumePath()),
      hasReference: caseVolumes.some((n) => n.id === get().referenceNodeId),
      busy: get().busy, jobRunning: state.job?.state === "running" || state.job?.state === "queued",
      engineReady: state.systemStatus?.engine.ready === true,
      pipelineKind: state.currentCase?.pipelineKind,
    }) ?? (tool ? parameterIssue(tool, get().fieldValues) : null);
    if (issue) {
      set({ notice: { tone: "error", message: issue, detail: null } });
      return;
    }
    if (!tool) {
      set({ notice: { tone: "info", message: "Select a tool first.", detail: null } });
      return;
    }
    if (tool.status === "future") {
      set({
        notice: {
          tone: "info",
          message: tool.comingSoon ?? "This tool is not implemented in v2 yet.",
          detail: tool.engine,
        },
      });
      return;
    }
    if (tool.inspect === "header") {
      await get().inspectHeader();
      return;
    }
    if (tool.op) {
      const manifest = useAppStore.getState().currentCase;
      const input = targetVolumePath();
      if (!manifest || !input) {
        set({ notice: { tone: "error", message: "Open a case and select a volume first.", detail: null } });
        return;
      }
      if (tool.needsReference && !get().referenceNodeId) {
        set({
          notice: {
            tone: "error",
            message: "This tool needs a second volume. Pick it under Parameters.",
            detail: null,
          },
        });
        return;
      }
      const params: Record<string, unknown> = { inputIsLabel: [...manifest.masks, ...manifest.atlases].some((n) => n.id === useAppStore.getState().selectedNodeId) };
      for (const field of tool.fields ?? []) {
        const raw = get().fieldValues[`${tool.id}.${field.id}`] ?? field.value;
        if (field.kind === "number" && !String(raw).includes(",")) params[field.id] = Number(raw);
        else params[field.id] = raw;
      }
      const inputNodeId = useAppStore.getState().selectedNodeId;
      const refNode = [...manifest.images, ...manifest.masks, ...manifest.atlases].find((n) => n.id === get().referenceNodeId);
      set({ busy: true, notice: null, opResult: null });
      try {
        const result = await runVolumeTool({
          caseId: manifest.caseId,
          op: tool.op,
          inputPath: input,
          referencePath: refNode ? nodeAbsPath(manifest, refNode) : null,
          params,
        });
        if (result.outputPath) {
          const updated = await refreshCaseNodes(manifest.caseId);
          if (useAppStore.getState().currentCase?.caseId !== manifest.caseId) {
            set({ busy: false });
            appActions.notify({ title: "Image operation completed", detail: result.outputPath, tone: "success" });
            return;
          }
          const previousSelection = useAppStore.getState().selectedNodeId;
          const followOutput = get().selectedId === tool.id && previousSelection === inputNodeId;
          usePipelineSession.getState().adoptCase(updated);
          if (!followOutput) appActions.setSelectedNodeId(previousSelection);
          const match = [...updated.images, ...updated.masks].find((n) => {
            const abs = nodeAbsPath(updated, n);
            return abs === result.outputPath;
          });
          if (match && followOutput) {
            appActions.setSelectedNodeId(match.id);
            if (updated.masks.some((n) => n.id === match.id)) {
              useViewerStore.getState().setOverlayNodes([match.id]);
              useViewerStore.getState().setOption("overlayVisible", true);
            } else useViewerStore.getState().setBaseNode(match.id);
          }
        }
        if (get().selectedId !== tool.id || useAppStore.getState().currentCase?.caseId !== manifest.caseId) { set({ busy: false }); return; }
        set({
          busy: false,
          opResult: result,
          notice: { tone: "info", message: result.message, detail: result.outputPath },
        });
      } catch (error) {
        const { message, detail } = splitError(error);
        set({ busy: false });
        if (get().selectedId === tool.id && useAppStore.getState().currentCase?.caseId === manifest.caseId) set({ notice: { tone: "error", message, detail } });
        else appActions.notify({ title: "Image operation failed", detail: message, tone: "error" });
      }
      return;
    }
    if (tool.inspect === "results") {
      return;
    }
    if (tool.inspect === "viewer" || tool.inspect === "viewer-overlay") {
      appActions.setActiveTab("view");
      if (tool.inspect === "viewer-overlay") appActions.setToggle("view.overlay", true);
      return;
    }
    if (tool.stage) {
      openProtocolStep(tool.stage.modality, tool.stage.id);
    }
  },
}));

// Case and object inspection state must never bleed into another study.
useAppStore.subscribe((state, prev) => {
  if (state.currentCase?.caseId !== prev.currentCase?.caseId) {
    useToolSession.setState({ referenceNodeId: null, header: null, headerPath: null, opResult: null, notice: null, launchOpen: false });
  } else if (state.selectedNodeId !== prev.selectedNodeId) {
    useToolSession.setState({ header: null, headerPath: null });
  }
});
